import React, { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import type { ClubInfo } from "../../lib/use-club-info";
import { ClubSwitcher } from "./club-switcher";
import { SignalementFlagButton } from "../signalement/flag-button";

import { useTranslation } from "react-i18next";
interface HeaderBarProps {
  clubInfo: ClubInfo | null;
  onLogoClick: () => void;
  onMenuClick: () => void;
  /** Hide the hamburger below md when a bottom nav already exposes the menu. */
  hideMenuOnMobile?: boolean;
  /** Opens the report dialog; absent when reporting is not allowed (impersonation). */
  onReport?: () => void;
}

/** Header height on phones once the page scrolls; sticky page bars read it from --header-h. */
const COMPACT_HEIGHT = "3rem";

/**
 * True once the page scrolls past the top on a phone. The two thresholds keep the
 * header from flickering: shrinking it moves the content up, which moves scrollY.
 */
function useCompactHeader(): boolean {
  const [compact, setCompact] = useState(false);
  useEffect(() => {
    const phone = window.matchMedia("(max-width: 639px)");
    let frame = 0;
    const update = () => {
      frame = 0;
      setCompact((was) => phone.matches && (was ? window.scrollY > 8 : window.scrollY > 56));
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    window.addEventListener("scroll", schedule, { passive: true });
    phone.addEventListener("change", schedule);
    update();
    return () => {
      window.removeEventListener("scroll", schedule);
      phone.removeEventListener("change", schedule);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);
  useEffect(() => {
    if (!compact) return;
    const root = document.documentElement;
    root.style.setProperty("--header-h", COMPACT_HEIGHT);
    return () => {
      root.style.removeProperty("--header-h");
    };
  }, [compact]);
  return compact;
}

export function HeaderBar({ clubInfo, onLogoClick, onMenuClick, hideMenuOnMobile, onReport }: HeaderBarProps) {
  const { t } = useTranslation("common");
  const compact = useCompactHeader();
  // A logo file that no longer loads falls back to the default mark instead of a broken image.
  const [failedLogo, setFailedLogo] = useState<string | null>(null);
  const logoUrl = clubInfo?.logoUrl && clubInfo.logoUrl !== failedLogo ? clubInfo.logoUrl : null;
  // Fixed sizes (not h-full) so the logo only ever moves between its two sizes while the bar animates.
  const logoSize = cn("shrink-0 self-center transition-[height,width] duration-200 sm:h-full sm:self-stretch", compact ? "h-8" : "h-10");
  return (
    <header className="sticky top-[var(--banner-h,0px)] z-30 border-b border-border bg-header/95 text-header-foreground backdrop-blur pt-[env(safe-area-inset-top)]">
      <div className={cn("flex items-stretch gap-2 px-4 transition-[height] duration-200 sm:h-20 sm:gap-3", compact ? "h-12" : "h-16")}>
        <Button variant="ghost" size="icon" onClick={onMenuClick} aria-label={t("headerBar.ouvrirLeMenu")} className={cn("hover:bg-header-foreground/10 hover:text-header-foreground", hideMenuOnMobile ? "hidden shrink-0 self-center md:inline-flex" : "shrink-0 self-center")}>
          <Menu className="h-5 w-5" />
        </Button>
        <button onClick={onLogoClick} className={cn("flex items-stretch gap-2.5 min-w-0 shrink", compact ? "py-1" : "py-1.5")}>
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={clubInfo?.name ?? ""}
              onError={() => setFailedLogo(logoUrl)}
              className={cn(logoSize, "w-auto max-w-[40vw] object-contain")}
            />
          ) : (
            <div className={cn(logoSize, "flex aspect-square flex-col items-center justify-center overflow-hidden rounded-full bg-primary text-primary-foreground font-bold leading-none ring-2 ring-header-foreground/20 [container-type:size]")}>
              {/* Glyphs scale with the circle (cqmin) so the stacked pair stays inside it at every bar size. */}
              <span className="text-[36cqmin]">稽</span>
              <span className="text-[36cqmin]">古</span>
            </div>
          )}
          <div className="min-w-0 self-center text-left">
            <h1 className={cn("font-bold leading-tight truncate transition-[font-size] sm:text-xl", compact ? "text-sm" : "text-base")}>{clubInfo?.name ?? ""}</h1>
            <p className="hidden text-xs opacity-70 sm:block">Keiko 稽古</p>
          </div>
        </button>
        <div className="flex items-center"><ClubSwitcher /></div>
        {onReport && (
          <div className="ml-auto flex items-center">
            <SignalementFlagButton onClick={onReport} className={compact ? "h-8 w-8 [&_svg]:h-4 [&_svg]:w-4" : undefined} />
          </div>
        )}
      </div>
    </header>
  );
}
